import axios from "axios";
import { useMutation, useQuery } from "react-query";
import { Config } from "../config";

const userMicroserviceBaseURL = Config.serviceUrls.userBaseUrl;

export async function getUser(userId) {
  var response = await axios.get(`${userMicroserviceBaseURL}/User/${userId}`);
  return response.data;
}

export function useUser(userId, options = {}) {
  return useQuery(["user", userId], () => getUser(userId), options);
}
export const credentialsLicensingGetByUserId = async (userId, ownerId) => {
  const ownerQuery = ownerId ? `?ownerId=${ownerId}` : "";
  const response = await axios.get(
    `${userMicroserviceBaseURL}/CredentialLicense/GetByUserId/${userId}${ownerQuery}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data.data;
};
export function useCredentialsLicensingGetByUserId(
  userId,
  ownerId,
  options = {},
) {
  return useQuery(
    ["user", userId, ownerId],
    () => credentialsLicensingGetByUserId(userId, ownerId),
    options,
  );
}

// GET CredentialLicense by ID
export async function getCredentialLicenseById(id) {
  const response = await axios.get(
    `${userMicroserviceBaseURL}/CredentialLicense/${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useCredentialLicenseById(id, options = {}) {
  return useQuery(
    ["credentialLicense", id],
    () => getCredentialLicenseById(id),
    {
      enabled: !!id,
      ...options,
    },
  );
}

// POST - Create CredentialLicense
export async function createCredentialLicense(data) {
  const response = await axios.post(
    `${userMicroserviceBaseURL}/CredentialLicense`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useCreateCredentialLicenseMutation() {
  return useMutation((data) => createCredentialLicense(data));
}

// PUT - Update CredentialLicense
export async function updateCredentialLicense(data) {
  const response = await axios.put(
    `${userMicroserviceBaseURL}/CredentialLicense`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useUpdateCredentialLicenseMutation() {
  return useMutation((data) => updateCredentialLicense(data));
}

// DELETE - Delete CredentialLicense
export async function deleteCredentialLicense(id) {
  const response = await axios.delete(
    `${userMicroserviceBaseURL}/CredentialLicense/${id}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data || response?.data?.message;
}

export function useDeleteCredentialLicenseMutation() {
  return useMutation((id) => deleteCredentialLicense(id));
}

// POST - Get All Credentials by Owner
export async function getAllCredentialsByOwner(data) {
  const response = await axios.post(
    `${userMicroserviceBaseURL}/CredentialLicense/GetAllByOwner`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export function useGetAllCredentialsByOwner(data, options = {}) {
  return useQuery(
    ["getAllCredentialsByOwner", data],
    () => getAllCredentialsByOwner(data),
    {
      enabled: !!data,
      ...options,
    },
  );
}

// POST - Get All Office Credentials by Owner
export async function getAllOfficeCredentialsByOwner(data) {
  const response = await axios.post(
    `${userMicroserviceBaseURL}/CredentialLicense/GetAllOfficeByOwner`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export function useGetAllOfficeCredentialsByOwner(data, options = {}) {
  return useQuery(
    ["getAllOfficeCredentialsByOwner", data],
    () => getAllOfficeCredentialsByOwner(data),
    {
      enabled: !!data,
      ...options,
    },
  );
}

export async function addLocation(countryId, stateId, cityId, address) {
  var response = await axios.post(
    `${userMicroserviceBaseURL}/User/addLocation`,
    {
      countryId,
      stateId,
      cityId,
      address,
    },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response.data.data;
}

export function useAddLocationMutation() {
  return useMutation(({ countryId, stateId, cityId, address }) =>
    addLocation(countryId, stateId, cityId, address),
  );
}

export async function deleteAccount() {
  var response = await axios.delete(
    `${userMicroserviceBaseURL}/User/FreeAccount`,
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }

  return response?.data?.message;
}

export function useDeleteAccountMutation(logout) {
  return useMutation(() => deleteAccount(), {
    onSuccess: () => {
      logout();
    },
  });
}
export async function neverShowUpdateInfoBar() {
  const response = await axios.patch(
    `${userMicroserviceBaseURL}/User/NeverShowUpdateInfoBar`,
    {
      NeverShowUpdateInfoBar: true,
    },
  );

  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}
export async function Logout() {
  var response = await axios.post(`${userMicroserviceBaseURL}/User/logout`, {
    isRequestFromWeb: true,
  });
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data;
}

export const sendMfaOpt = async (params, token, isAlternateMethod) => {
  const pathname = isAlternateMethod ? "SendSmsMfa" : "SendEmailMfa";
  const response = await axios.post(
    `${userMicroserviceBaseURL}/User/${pathname}`,
    { ...params },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return response.data;
};

export const verifyMfaOpt = async (params, token) => {
  const response = await axios.post(
    `${userMicroserviceBaseURL}/User/VerifyMfaOtp`,
    { ...params },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  return response.data;
};

// GET - Get Credential License Notification Preference
export async function getCredentialLicenseNotificationPreference(ownerId) {
  const response = await axios.get(
    `${userMicroserviceBaseURL}/Preferences/CredentialLicenseNotificationPreference?ownerId=${ownerId}`,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useCredentialLicenseNotificationPreference(
  ownerId,
  options = {},
) {
  return useQuery(
    ["credentialLicenseNotificationPreference", ownerId],
    () => getCredentialLicenseNotificationPreference(ownerId),
    {
      enabled: !!ownerId,
      ...options,
    },
  );
}

// PUT - Update Credential License Notification Preference
export async function updateCredentialLicenseNotificationPreference(data) {
  const response = await axios.put(
    `${userMicroserviceBaseURL}/Preferences/CredentialLicenseNotificationPreference`,
    data,
  );
  if (response.data.statusCode !== 200) {
    throw new Error(response?.data?.message);
  }
  return response.data.data;
}

export function useUpdateCredentialLicenseNotificationPreferenceMutation() {
  return useMutation((data) =>
    updateCredentialLicenseNotificationPreference(data),
  );
}
